import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistMissionsForGuestQuery,
  ArtistMissionsForGuestQueryVariables,
  ArtistMissionsQuery,
  ArtistMissionsQueryVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistMissionsQuery,
  FetchNewArtistMissionsQueryVariables,
  MissionBoxFragmentDoc,
  MissionsConnectionFragmentDoc,
  UserStepMissionBoxFragmentDoc,
  UserStepMissionsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { MissionType } from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user, userClaims } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistMissionsQuery,
    FetchNewArtistMissionsQueryVariables
  >({
    query: FETCH_NEW_MISSIONS,
    variables: {
      missionsArtistId: artistId,
      missionsTypeFilter: [MissionType.Quiz, MissionType.ItemCollect],
    },
    fields: [
      {
        fragmentName: 'MissionBox',
        fieldName: 'missionsWithFilteres',
        fragment: MissionBoxFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, refetch, fetchMore, networkStatus, loading } = useQuery<
    ArtistMissionsQuery,
    ArtistMissionsQueryVariables
  >(QUERY, {
    variables: {
      missionsArtistId: artistId,
      missionsTypeFilter: [
        MissionType.Quiz,
        MissionType.ItemCollect,
        /**
         * @description
         * twitter機能開放時に追加
         * https://app.clickup.com/t/908127/UT-3968
         *
         * MissionType.TwitterRetweet,
         * MissionType.TwitterFollow
         */
      ],
    },
    skip: !user,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const {
    data: dataGuest,
    refetch: refetchGurest,
    fetchMore: fetchMoreGurest,
    loading: loadingGurest,
    networkStatus: networkStatusGurest,
  } = useQuery<
    ArtistMissionsForGuestQuery,
    ArtistMissionsForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      missionsArtistId: artistId,
      missionsTypeFilter: [
        MissionType.Quiz,
        MissionType.ItemCollect,
        /**
         * @description
         * twitter機能開放時に追加
         * https://app.clickup.com/t/908127/UT-3968
         *
         * MissionType.TwitterRetweet,
         * MissionType.TwitterFollow
         */
      ],
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !!user,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!(data || dataGuest)

  const [getStepMissionReward] = useMutation(GET_STEP_MISSION_REWARD)

  return {
    state: {
      loading: (loadingGurest || loading) && !isNextFetch,
      fetching:
        networkStatus === NetworkStatus.fetchMore ||
        networkStatusGurest === NetworkStatus.fetchMore,
      artistName: (data || dataGuest)?.artist?.displayName || '',
      hasNextPage: !!(data || dataGuest)?.missionsWithFilteres?.pageInfo
        .hasNextPage,
      hasNextPageArtistStepMission:
        !!data?.artistStepMissions?.pageInfo.hasNextPage,
      missions: (data || dataGuest)?.missionsWithFilteres ?? null,
      user: data?.user ?? null,
      artistId,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      liveStreamId: (data || dataGuest)?.artist?.currentLive?.id,
      artistStepMissions: data?.artistStepMissions ?? null,
    },
    actions: {
      async onFilterArtistMission(missionTypes) {
        if (user) {
          await refetch({
            missionsArtistId: artistId,
            missionsTypeFilter: missionTypes,
          })
          return
        }

        await refetchGurest({
          missionsArtistId: artistId,
          missionsTypeFilter: missionTypes,
        })
      },
      async onGetStepMissionReward(stepMissionId: string) {
        await getStepMissionReward({ variables: { stepMissionId } })
      },
      async onFetchMoreArtistMissions(missionTypes) {
        if (user) {
          await fetchMore({
            variables: {
              missionsArtistId: artistId,
              missionsTypeFilter: missionTypes,
              missionsPageInput: {
                cursor: data?.missionsWithFilteres?.pageInfo?.endCursor,
              },
            },
          })
          return
        }
        await fetchMoreGurest({
          variables: {
            missionsArtistId: artistId,
            missionsTypeFilter: missionTypes,
            missionsPageInput: {
              cursor: dataGuest?.missionsWithFilteres?.pageInfo?.endCursor,
            },
          },
        })
      },
      async onFetchMoreStepMission() {
        await fetchMore({
          variables: {
            missionsArtistId: artistId,
            missionsTypeFilter: [MissionType.Quiz, MissionType.ItemCollect],
            stepMissionsPageInput: {
              cursor: data?.artistStepMissions?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistMissions(
    $missionsTypeFilter: [MissionType!]
    $missionsPageInput: PageInput
    $missionsArtistId: ID!
    $stepMissionsType: StepMissionType
    $stepMissionsFilter: StepMissionFilter
    $stepMissionsPageInput: PageInput
  ) {
    user {
      ...CurrentUser
    }
    missionsWithFilteres(
      typeFilter: $missionsTypeFilter
      pageInput: $missionsPageInput
      artistId: $missionsArtistId
    ) {
      ...MissionsConnection
    }
    artist(id: $missionsArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    artistStepMissions(
      artistId: $missionsArtistId
      type: $stepMissionsType
      filter: $stepMissionsFilter
      pageInput: $stepMissionsPageInput
    ) {
      ...UserStepMissionsConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${MissionsConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${UserStepMissionsConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query ArtistMissionsForGuest(
    $missionsTypeFilter: [MissionType!]
    $missionsPageInput: PageInput
    $missionsArtistId: ID!
  ) {
    missionsWithFilteres(
      typeFilter: $missionsTypeFilter
      pageInput: $missionsPageInput
      artistId: $missionsArtistId
    ) {
      ...MissionsConnection
    }
    artist(id: $missionsArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${MissionsConnectionFragmentDoc}
`

const FETCH_NEW_MISSIONS = gql`
  query FetchNewArtistMissions(
    $missionsTypeFilter: [MissionType!]
    $missionsPageInput: PageInput
    $missionsArtistId: ID!
  ) {
    artist(id: $missionsArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    missionsWithFilteres(
      typeFilter: $missionsTypeFilter
      pageInput: $missionsPageInput
      artistId: $missionsArtistId
    ) {
      ...MissionsConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${MissionsConnectionFragmentDoc}
`

const GET_STEP_MISSION_REWARD = gql`
  mutation GetStepMissionReward($stepMissionId: String!) {
    getStepMissionReward(stepMissionId: $stepMissionId) {
      ...UserStepMissionBox
    }
  }
  ${UserStepMissionBoxFragmentDoc}
`
